<template>
	<li class="base-menu-item" :class="$attrs.class">
		<component
			:is="tag"
			v-if="hasActivator"
			ref="tag"
			v-bind="{ ...omit($attrs, ['class']), ...tagProps }"
			class="base-menu-item__activator"
			:class="{ 'base-menu-item__activator--is-clickable': isClickable }"
		>
			<div v-if="hasLabel" class="base-menu-item__label">
				<template v-if="label">
					{{ label }}
				</template>

				<slot v-else name="label" />
			</div>
		</component>

		<slot />
	</li>
</template>

<script lang="ts">
import { omit } from 'lodash';
import { defineComponent, PropType } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import { useHasSlot } from '@/composables';
import { AnyObject } from '@/tools/types';

export default defineComponent({
	inheritAttrs: false,

	props: {
		button: {
			type: Boolean,
			default: false,
		},

		to: {
			type: [String, Object] as PropType<RouteLocationRaw | null>,
			default: null,
		},

		href: {
			type: String,
			default: null,
		},

		label: {
			type: String,
			default: null,
		},
	},

	emits: ['click'],

	setup(props, { slots }) {
		return {
			omit,

			hasLabel: useHasSlot('label', slots, hasSlot => hasSlot || !!props.label),
		};
	},

	computed: {
		tag(): string {
			if (this.to) {
				return 'router-link';
			} else if (this.href) {
				return 'a';
			} else if (this.button) {
				return 'button';
			}

			return 'span';
		},

		tagProps(): AnyObject {
			const props: AnyObject = {};

			if (this.button) {
				props['type'] = 'button';
				props['onClick'] = () => this.$emit('click');
			}

			if (this.to) {
				return {
					...props,
					to: this.to,
				};
			}

			return {
				...props,
				href: this.href,
			};
		},

		isClickable(): boolean {
			return (
				this.hasActivator &&
				(['a', 'router-link', 'button'].includes(this.tag) || !!this.$attrs.onClick)
			);
		},

		hasActivator(): boolean {
			return this.hasLabel;
		},
	},
});
</script>

<style lang="scss">
.base-menu-item {
	display: block;

	&__activator {
		display: flex;
		padding: 1.6rem 0;
		text-decoration: none;
	}

	> button {
		width: 100%;
	}
}
</style>
